import { useToasts } from "@certn/cascadia";
import {
  Divider,
  GridItem,
  Icon,
  CodeInput,
  Text,
  Link,
} from "@certn/cascadia-react";
import { APIError } from "@certn/legacy-api";
import { useGoogleAnalytics } from "@certn/utils";
import qs from "qs";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

import { AuthHeader } from "./AuthHeading";
import { useAuth } from "./hooks";
import { usePassCodeCreate, usePassCodeVerify } from "./passcodeHooks";
import { useSettingsDropdownStyles } from "../../components/AppHeader/SettingsDropdown/styles";

const RESEND_TIME = 60 * 2; // 2 minutes

export function CodeInputPage() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const { gaTrackEvent } = useGoogleAnalytics();
  const navigate = useNavigate();
  const [isSuccess, setIsSuccess] = useState(false);
  const [isFail, setIsFail] = useState(false);
  const { show } = useToasts();
  const [resendTime, setResendTime] = useState<number>(RESEND_TIME);

  const styles = useSettingsDropdownStyles();

  const submit = usePassCodeCreate();
  const { verify } = usePassCodeVerify();
  const location = useLocation();

  const userEmail = location.state?.["userEmail"];
  const [passCode, setPassCode] = useState("");
  const valueLength = 6;

  const handleResendCode = () => {
    if (userEmail) {
      submit(userEmail);
      show({ title: t("Email resent"), type: "info" });
      setResendTime(RESEND_TIME);
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setResendTime((prev) => Math.max(prev - 1, 0));
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [setResendTime]);

  const resendMinutesLeft = Math.floor(resendTime / 60);
  const resendSecondsLeft = resendTime % 60;

  const onSuccessLogin = async (token: string) => {
    try {
      await login(token);
      setIsSuccess(true);
      gaTrackEvent("login", { method: "Pass code" });

      const queryParams = qs.parse(location.search, {
        ignoreQueryPrefix: true,
      });
      await new Promise((_) => setTimeout(_, 500));

      navigate(
        typeof queryParams.redirect === "string" ? queryParams.redirect : "/"
      );
    } catch (e) {
      if (!(e instanceof APIError)) {
        console.error("Error when trying to log in:", e);
      }
      setIsFail(true);
    }
    setPassCode("");
  };

  useEffect(() => {
    // need to wait for passCode to populate in state before validating.
    if (passCode.length === valueLength) {
      verify(
        onSuccessLogin,
        setPassCode,
        setIsFail,
        setIsSuccess,
        userEmail,
        passCode
      );
    } else if (passCode.length > 0) {
      setIsFail(false);
      setIsSuccess(false);
    }
  }, [passCode]);

  const onChange = (value: string) => {
    setPassCode(value);
  };

  if (!userEmail) {
    return <Navigate to="/auth/welcome" />;
  }

  return (
    <>
      <Link
        onPress={() => navigate(`/auth/welcome${location.search}`)}
        testID="enter-code-screen-back-button"
      >
        <Icon name="arrow_back" color="icon-base" />
      </Link>
      <AuthHeader
        title={t("Enter 6-digit code")}
        subtitle={
          <Text style="form/body-primary">
            <>
              <Trans>
                To continue, verify your email by entering the code sent to{" "}
              </Trans>
              <Text style="form/section-heading">{userEmail}</Text>
            </>
          </Text>
        }
      />
      <GridItem alignItems="center">
        <CodeInput
          value={passCode}
          valueLength={valueLength}
          onChange={onChange}
          isSuccess={isSuccess}
          isFail={isFail}
          testID="enter-code-screen-input"
        />
      </GridItem>

      <div className={styles.divider} style={{ justifyContent: "center" }}>
        <Divider />
      </div>

      <GridItem>
        <Text style="form/body-primary" align="center" marginTop="space-0">
          {t("Didn't receive an email?")}{" "}
        </Text>

        {resendTime === 0 ? (
          <Link
            onPress={handleResendCode}
            textColor="text-base"
            style={{
              justifyContent: "center",
              alignItems: "center",
              display: "flex",
            }}
            testID="enter-code-screen-resend-code-button"
          >
            {t("Resend code")}
          </Link>
        ) : (
          <Text style="form/body-primary" align="center">
            {t("Resend code in {{time}}", {
              time: `${resendMinutesLeft}:${
                resendSecondsLeft < 10
                  ? "0" + resendSecondsLeft
                  : resendSecondsLeft
              }`,
            })}
          </Text>
        )}
      </GridItem>
    </>
  );
}
